/*
 * @Author: fengyuhang
 * @Date: 2023-07-10 10:50:06
 * @LastEditTime: 2025-02-27 18:09:18
 * @LastEditors: fengyuhang
 */

export type UpdaterOptions = Partial<{
  /** 检测更新的间隔 */
  loopInterval: number;
  /** 是否触发更新的 meta 字段 */
  shouldAlertUpdateMetaName: string;
  /** 更新日志 meta 字段 */
  updateLogMetaName: string;
}>;

type UpdaterEvents = 'update' | 'not-update';

export class Updater {
  static SubscribeCount = 0;
  /**
   * 匹配script标签
   */
  static ScriptMatchReg = new RegExp(/<script(?:\s+[^>]*)?>(.*?)<\/script\s*>/gi);
  /**
   * 旧的script的hash信息
   */
  oldScript: string[];
  /**
   * 发布订阅通知用户更新
   */
  eventFnList: Record<UpdaterEvents, Function[]>;
  timerId: NodeJS.Timeout | null;
  hasClear: boolean;
  /** 是否触发更新的 meta 字段 */
  shouldAlertUpdateMetaName: string;
  /** 更新日志的 meta 字段 */
  updateLogMetaName: string;

  constructor(options?: UpdaterOptions) {
    this.oldScript = [];
    this.eventFnList = {
      update: [],
      'not-update': [],
    };
    this.timerId = null;
    this.hasClear = false;
    this.shouldAlertUpdateMetaName = options?.shouldAlertUpdateMetaName as string;
    this.updateLogMetaName = options?.updateLogMetaName as string;

    if (Updater.SubscribeCount === 0) {
      this.init();
      this.startLoopInterval(options?.loopInterval);
      Updater.SubscribeCount++;
    } else {
      console.warn('[eevee-hooks: useUpdateProgram]: Each application should have only one updater instance');
    }
  }

  private async init() {
    const html: string = await this.getHtml();
    this.oldScript = this.parserScript(html);
  }

  private async getHtml() {
    try {
      const html = await fetch('/').then(res => res.text()); // 读取index.html
      return html;
    } catch (error: any) {
      console.error(`[eevee-hooks: useUpdateProgram]: ${error?.toString()}`);
      clearInterval(this.timerId as NodeJS.Timeout);
      return 'error';
    }
  }

  private parserScript(html: string) {
    return html?.match(Updater.ScriptMatchReg) as string[];
  }

  private compareScript(oldScript: string[], newHtml: string) {
    const newScript = this.parserScript(newHtml);

    /** 解析不出script值时，如DNS解析报错，则不执行更新逻辑 */
    if (!newScript) return;

    const latestScript = Array.from(new Set((oldScript || [])?.concat(newScript)));

    // 如果新旧length一样说明无更新
    if (oldScript.length === latestScript?.length) {
      this.eventFnList['not-update']?.forEach(fn => fn?.());
    } else {
      /** 根据当前的meta配置判断是否需要提示更新 */
      const appShouldAlertUpdate = this.parseMetaContent(this.shouldAlertUpdateMetaName, newHtml);

      // 只要不是解析到false都提示更新
      if (appShouldAlertUpdate !== 'false') {
        /** 从 meta 中提取更新日志 */
        const updateLog = this.parseMetaContent(this.updateLogMetaName, newHtml);

        this.eventFnList['update']?.forEach(fn => fn?.(updateLog || null));
      } else {
        console.log('检测到更新，但已配置忽略更新。');
      }
    }
  }

  private startLoopInterval(intervalTime = 60 * 1000) {
    this.timerId = setInterval(async () => {
      const newHtml = await this.getHtml();

      if (newHtml === 'error') return;
      this.compareScript(this.oldScript, newHtml);
    }, intervalTime);
  }

  public async handleCompare() {
    if (this.hasClear) return;

    const newHtml = await this.getHtml();
    if (newHtml === 'error') return;

    this.compareScript(this.oldScript, newHtml);
  }

  /** 从meta解析对应name的content字段 */
  private parseMetaContent(metaName: string, htmlContent: string) {
    if (!(metaName && htmlContent)) return null;

    const metaRegex = new RegExp(`<meta\\s+name="${metaName}"\\s+content="([^"]*)"\\s*\\/?>`);
    const match = htmlContent.match(metaRegex);

    return match ? match?.[1] : null;
  }

  // 发布订阅通知
  on(eventName: UpdaterEvents, fn: Function) {
    (this.eventFnList[eventName] || (this.eventFnList[eventName] = [])).push(fn);
    return this;
  }

  // 监听一次
  once(eventName: UpdaterEvents, fn: Function) {
    // 先绑定，调用后删除
    const eventFunc = (...args: any[]) => {
      this.off(eventName, eventFunc);
      fn.apply(this, args);
    };
    this.on(eventName, eventFunc);
    return this;
  }

  // 取消订阅
  off(eventName: UpdaterEvents, fn: Function) {
    const fns = this.eventFnList[eventName];
    // 如果缓存列表中没有相应的 fn，返回false
    if (!fns) return false;
    if (!fn) {
      // 如果没有传 fn 的话，就会将 event 值对应缓存列表中的 fn 都清空
      fns && (fns.length = 0);
    } else {
      // 若有 fn，遍历缓存列表，看看传入的 fn 与哪个函数相同，如果相同就直接从缓存列表中删掉以取消订阅
      let cb: Function;
      for (let i = 0; i < fns.length; i++) {
        cb = fns[i];
        // eslint-disable-next-line max-depth
        if (cb === fn) {
          fns.splice(i, 1);
          break;
        }
      }
    }
    return this;
  }
  // 取消订阅更新
  clear() {
    if (!this.hasClear) {
      this.timerId && clearInterval(this.timerId);
      Updater.SubscribeCount--;
      this.eventFnList = {
        update: [],
        'not-update': [],
      };
    }
    this.hasClear = true;
  }
}
